import { type Color, type GameResult, type Score as EngineScore } from '@chess/api-model';
import { Score, positionAt, referenceOf, sideToMove, viewPly, type ReviewState } from '@chess/api-review';
import type { ShortlistReading } from '@chess/api-review/board';

// Where the bar stands and what it says, after v1's AdvantageReading. The
// bar fills from the bottom up with the bottom player's advantage: the
// question a person asks of a bar beside their own board is "am I doing
// well?", not "is White doing well?". This is the one place the score and
// the board's orientation meet.

export interface Reading {
  /** The share of the bar the bottom player fills, 0…1; 0.5 is level. */
  share: number;
  /** The score as a person reads it, from the bottom player's side: "+0.34", "-1.20", "M4", "-M2". */
  text: string;
}

// The score, its sign, its text and the share of the bar come from the
// review API's `Score`, the same object every other panel formats with.

/** A score for the side to move, turned to White's point of view. */
export function forWhite(score: EngineScore, sideToMove: Color): EngineScore {
  return Score.fromEngine(score, sideToMove, 'white').toJSON();
}

/**
 * The reading of the initial position: level, whatever the engine thinks of
 * the first move. The game has not started, so nobody is ahead.
 */
export function readingAtStart(bottom: Color): Reading {
  return readingOf({ cp: 0 }, bottom);
}

/**
 * The reading of a White-relative score from the reference player's side:
 * the reader's, whatever the board's orientation. A positive text means the
 * reference player is better, and `share` is the reference player's share.
 */
export function readingOf(scoreForWhite: EngineScore, bottom: Color): Reading {
  const seen = Score.fromEngine(scoreForWhite, 'white', bottom);
  return { share: seen.share(), text: seen.text() };
}

/**
 * The reading of a game that is over: full for the winner, halfway for a
 * draw, said without the engine. An unfinished result gives nothing.
 */
export function readingOfResult(result: GameResult, bottom: Color): Reading | undefined {
  if (result === '1/2-1/2') return { share: 0.5, text: '½-½' };
  if (result === '1-0') return { share: bottom === 'white' ? 1 : 0, text: '1-0' };
  if (result === '0-1') return { share: bottom === 'black' ? 1 : 0, text: '0-1' };
  return undefined;
}

/**
 * What the bar says for the position on the board: level at the start, the
 * shortlist's live reading when it is about the same position, nothing else
 * on a line of the reader's own, the result on a finished game's last
 * position, else the stored scan's reading. The position is the one the
 * reader looks at, so a look back reads the earlier position, not the head.
 * Scores are read for the reader, whichever side is at the bottom of the
 * board.
 */
export function readingFor(state: ReviewState, shortlist: ShortlistReading): Reading | undefined {
  const fen = positionAt(state);
  if (!fen || !state.game) return undefined;
  const bottom = referenceOf(state.game.sides);
  const ply = viewPly(state);
  // A look back along a line to where it left the game is on the game again.
  const onGame = !state.exploring || ply <= state.exploring.fromPly;
  // The initial position is always level: no live or stored reading overrides it.
  if (onGame && ply === 0) return readingAtStart(bottom);
  if (shortlist.fen === fen && shortlist.best?.score) {
    return readingOf(forWhite(shortlist.best.score, sideToMove(fen)), bottom);
  }
  if (!onGame) return undefined;
  if (ply === state.game.moves.length) {
    const ended = readingOfResult(state.game.result, bottom);
    if (ended) return ended;
  }
  const here = state.scan?.position(ply);
  if (!here) return undefined;
  return readingOf(forWhite(here.score, sideToMove(here.fen)), bottom);
}

export { sideToMove };
